import PropTypes from 'prop-types';
import cx from 'classnames';

import FontSizeChanger from 'core/components/FontSizeChanger';
import Link from 'core/components/Link';
import withTheme from 'core/hocs/withTheme';
import withTopic from 'core/hocs/withTopic';
import Authors from 'core/components/Topic/Authors';
import ReadDuration from 'core/components/ReadDuration';

import themePropTypes from 'core/utils/prop-types/theme';

import resolveRelationships from 'core/utils/relationships';
import { topicFullDateFormat } from 'core/utils/dates';
import { buildRubricUrl } from 'core/utils/url-helper';
import { resolveScopedStyles } from 'core/utils/styled-jsx';

import styles from './index.styl';

const relationships = resolveRelationships(
  ['rubric', 'authors'],
  {},
  {}
);

/**
 *
 * @author Evgeniy Kovalev <e.kovalev@rambler-co.ru>
 */
function TopicTopline(props) {
  const {
    topic: {
      content,
      infinityIndex,
    },
    theme: {
      animations,
      colors,
      controls: {
        topicTopline: toplineAtoms,
      },
    },
    path,
    hideFontSizeChanger,
    center,
    isPremiumTopic,
    isShowAuthors,
  } = props;

  const {
    attributes: {
      published_at: publishedAt,
      ad_label: adLabel,
      read_duration: readDuration,
    },
  } = content;

  const {
    rubric: {
      title: rubricTitle,
      slug: rubricSlug,
      root_slug: rubricRoot,
    },
    authors,
  } = relationships(content);

  const hideFontSizeControls = hideFontSizeChanger || infinityIndex > 0;

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .${styles.topline}
          font ${toplineAtoms.font}
          color ${toplineAtoms.color}
          text-transform ${toplineAtoms.textTransform || 'none'}
          letter-spacing ${toplineAtoms.letterSpacing || 'normal'}

        .${styles.rubric}
          font ${toplineAtoms.rubric.font}
          color ${toplineAtoms.rubric.color}
          padding ${toplineAtoms.rubric.padding}
          background ${toplineAtoms.rubric.background}
          border ${toplineAtoms.rubric.border}
          border-bottom ${toplineAtoms.rubric.borderBottom}
          border-radius ${toplineAtoms.rubric.borderRadius}
          text-decoration ${toplineAtoms.rubric.decoration}
          text-transform ${toplineAtoms.rubric.textTransform}
          letter-spacing ${toplineAtoms.rubric.letterSpacing}
          transition all ${animations.hover}
          &:visited
            color ${toplineAtoms.rubric.color}
            background ${toplineAtoms.rubric.background}
            text-decoration ${toplineAtoms.rubric.decoration}
          &:hover
            color ${toplineAtoms.rubric.hover.color}
            background ${toplineAtoms.rubric.hover.background}
            border-bottom ${toplineAtoms.rubric.hover.borderBottom}
            text-decoration ${toplineAtoms.rubric.hover.decoration}
          &:before
            content '${toplineAtoms.rubric.before ? toplineAtoms.rubric.before.content : ''}'
          &:after
            content '${toplineAtoms.rubric.after ? toplineAtoms.rubric.after.content : ''}'
          .${styles._premium} &
            border none
            color ${toplineAtoms.rubric.premiumTopic.color}

        .${styles.adLabel}
          &:before
          &:after
            background-color ${colors.divider}
      `}</style>
    </scope>
  );

  return (
    <div className={scope.wrapClassNames(
      styles.topline,
      center && styles._toplineCenter,
      isShowAuthors && styles._isShowAuthors,
      isPremiumTopic && styles._premium,
    )}
    >
      <div className={cx(styles.item, !hideFontSizeControls && styles.rubricWrapper)}>
        <Link
          className={scope.wrapClassNames(styles.rubric)}
          to={buildRubricUrl(rubricSlug, rubricRoot, path)}
        >
          {rubricTitle}
        </Link>
      </div>
      {!isPremiumTopic && (
        <>
          {publishedAt && (
            <div className={cx(styles.item, styles.publishedDate)}>
              Опубликовано {topicFullDateFormat(publishedAt)}
            </div>
          )}
          {readDuration && (
            <ReadDuration
              className={cx(styles.item, styles.readDuration)}
              readDuration={readDuration}
            />
          )}
          {adLabel && (
            <div className={scope.wrapClassNames(
              styles.item,
              styles.adLabel
            )}
            >
              {adLabel}
            </div>
          )}
          {!hideFontSizeControls && (
            <div className={cx(styles.item, styles.fontChanger)}>
              <FontSizeChanger />
            </div>
          )}
          {(authors.length > 0 && isShowAuthors) && (
            <div className={cx(styles.authors, styles.item)}>
              <Authors authors={authors} />
            </div>
          )}
        </>
      )}
      <scope.styles />
    </div>
  );
}

TopicTopline.propTypes = {
  /**
   * Премиум топик
   */
  isPremiumTopic: PropTypes.bool,
  /**
   * Отображение автора в топлайне
   */
  isShowAuthors: PropTypes.bool,
  /**
   * Отображение топлайна по центру родительского блока
   */
  center: PropTypes.bool,

  /**
   * Данные топика
   */
  topic: PropTypes.object.isRequired,

  /**
   * Скрытие кнопок уменьшения/увеличения шрифта в топике
   */
  hideFontSizeChanger: PropTypes.bool,

  /**
   * Префикс пути для проектов с раздельной сервисной/медийной частями
   *
   * TODO: переделать на `context.mediaUrlPrefix`?
   */
  path: PropTypes.string,

  /** @ignore */
  theme: themePropTypes(`{
    animations: {
      hover,
    },
    colors: {
      divider,
      layout
    },
    controls: {
      topicTopline: {
        font,
        color,
        rubric: {
          color,
          border,
          borderRadius,
          padding,
          hover: {
            color,
            background
          },
        }
      }
    }
  }`),
};

const TopicToplineWithHOCs = withTheme(withTopic(TopicTopline));
TopicToplineWithHOCs.displayName = 'TopicTopline';

export { TopicTopline as StorybookComponent };
export default TopicToplineWithHOCs;
